import React from 'react';
import { Box } from '@mui/system';
import useStyles from './styles';
import { Grid, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { FormGroup } from '@mui/material';

import { usePageLevelVideoConfigContext } from '../../../../../../../Contexts/PageLevelVideoConfigContext';
import InputConfig from '../../../../../../../Components/InputConfig';
import DropDownSingle from '../../../../../../../Components/DropDownSingle';
import { CTA_TYPES, PAGE_LEVEL_VIDEO_CTA } from '../../../../../../../core/constants';
import {
	getCtaErrors,
	getVideoCtaDetails,
	getVideoCtaStatus,
	updatePlaylistVideo
} from '../../../../../../../utilities/pageLevelVideoCta';

const { TEXT_MAX_LENGTH } = PAGE_LEVEL_VIDEO_CTA;
const isPagePath = (value) => value?.startsWith('/') && !value.startsWith('//');

/*
 * Renders a configuration section for a single page.
 */
const SinglePageConfig = ({ config }) => {
	const classes = useStyles();
	// Keep repair access for this editing session, even after the first valid slash.
	const [canRepairPagePath] = React.useState(() => !isPagePath(config?.pageUrl));
	const {
		handlePlaylistConfigAddVideo,
		handlePlaylistConfigRemoveVideo,
		setPlaylistConfigData,
		setPlaylistConfigError
	} = usePageLevelVideoConfigContext();

	if (!config) {
		setPlaylistConfigError(true);
	}

	/**
	 * Handler to change specific video field values.
	 *
	 * @param {number} videoIndex - Index of the video in the config.
	 * @param {Object} changes - Fields to merge into the selected video.
	 */
	const handleVideoFieldChange = (videoIndex, changes) => {
		/**
		 * Updates the video object with the provided changes.
		 * @param {Object} video - The current video object.
		 * @returns {Object} - The updated video object.
		 */
		function updateVideo(video) {
			return {
				...video,
				...changes
			};
		}
		setPlaylistConfigData((prevConfigData) =>
			updatePlaylistVideo(prevConfigData, config.id, videoIndex, updateVideo)
		);
	};

	/**
	 * Handler to change the page URL.
	 *
	 * @param {string} value - New value for the page URL.
	 */
	const handlePageUrlChange = (value) => {
		setPlaylistConfigData((prevConfigData) =>
			prevConfigData.map((page) => (page.id === config.id ? { ...page, pageUrl: value } : page))
		);
	};

	const hasValidPagePath = isPagePath(config?.pageUrl);
	const canEditPage = !!config.isDeletable || canRepairPagePath;

	return (
		config && (
			<Box>
				<FormGroup
					style={{
						margin: '1rem 0 0 0',
						display: 'flex',
						justifyContent: 'center'
					}}
				>
					<Grid container spacing={2} className={classes.inputConfig}>
						<Grid item xs={12}>
							<InputConfig
								isObjectEditor={false}
								label="Page path"
								inputProps={{ 'aria-label': 'Page path' }}
								placeholder="/blog/posts/article"
								error={!!config.pageUrl && !hasValidPagePath}
								helperText="Use only the path starting with /, without the domain."
								directValue={config.pageUrl}
								updateDirectValue={(value) => handlePageUrlChange(value)}
								inputStyles={{ width: '100%', margin: '1rem 0' }}
								editable={canEditPage}
								labelStyles={{
									fontSize: '2.4rem',
									lineHeight: '1.334',
									textAlign: 'left',
									width: '100%'
								}}
							/>
						</Grid>
					</Grid>

					<Box
						container
						sx={{
							height: '70vh',
							overflowX: 'scroll',
							border: '1px solid rgba(0, 0, 0, 0.38)',
							borderRadius: '4px',
							paddingX: '1rem',
							paddingY: '1rem'
						}}
					>
						{config &&
							config.videos &&
							config.videos.map((video, videoIndex) => {
								const ctaDetails = getVideoCtaDetails(video);
								const ctaErrors = getCtaErrors(ctaDetails);
								return (
									<React.Fragment key={videoIndex}>
										<Box
											style={{
												border: '1px solid rgba(0, 0, 0, 0.38)',
												padding: '1.5rem',
												marginBottom: '1.5rem',
												borderRadius: '4px'
											}}
										>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between'
												}}
											>
												<Typography variant="h6" fontWeight={600}>
													Video {videoIndex + 1}
												</Typography>
												<IconButton
													color="error"
													aria-label={`Delete video ${videoIndex + 1}`}
													title="Delete video"
													onClick={() => handlePlaylistConfigRemoveVideo(config.id, videoIndex)}
												>
													<DeleteIcon />
												</IconButton>
											</Box>
											<Box className={classes.videoConfig}>
												<Grid container spacing={2}>
													<Grid item xs={4}>
														<FormGroup>
															<InputConfig
																sx={{ width: '100%' }}
																isObjectEditor={false}
																label="Video URL"
																labelStyles={{
																	fontSize: '2.4rem',
																	lineHeight: '1.334',
																	textAlign: 'left',
																	width: '100%'
																}}
																directValue={video.videoUrl}
																updateDirectValue={(value) =>
																	handleVideoFieldChange(videoIndex, { videoUrl: value })
																}
																inputStyles={{ width: '100%' }}
																editable={true}
															/>
														</FormGroup>
													</Grid>
													<Grid item xs={4}>
														<DropDownSingle
															label="Video Type"
															list={['video/mp4', 'application/x-mpegURL']}
															isObjectEditor={false}
															directValue={video.videoType}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, { videoType: value })
															}
															formControlStyles={{ width: '100%' }}
															formControlVariant={'outlined'}
															labelStyles={{
																fontSize: '2.4rem',
																lineHeight: '1.334',
																textAlign: 'left',
																width: '100%'
															}}
														/>
													</Grid>
													<Grid item xs={4}>
														<InputConfig
															isObjectEditor={false}
															label="No of Times"
															inputType="number"
															labelStyles={{
																fontSize: '2.4rem',
																lineHeight: '1.334',
																textAlign: 'left',
																width: '100%'
															}}
															directValue={video.numberOfTimes}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, { numberOfTimes: value })
															}
															inputStyles={{ width: '100%' }}
															editable={true}
														/>
													</Grid>
												</Grid>
												<Grid container spacing={2} sx={{ marginTop: '1rem' }}>
													<Grid item xs={6}>
														<InputConfig
															isObjectEditor={false}
															label={`Description`}
															directValue={video.description}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, { description: value })
															}
															labelStyles={{
																fontSize: '2.4rem',
																lineHeight: '1.334',
																textAlign: 'left',
																width: '100%'
															}}
															inputStyles={{ width: '100%', marginBottom: '1rem' }}
															editable={true}
														/>
													</Grid>
													<Grid item xs={6}>
														<InputConfig
															isObjectEditor={false}
															label={`Thumbnail`}
															directValue={video.thumbnail}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, { thumbnail: value })
															}
															labelStyles={{
																fontSize: '2.4rem',
																lineHeight: '1.334',
																textAlign: 'left',
																width: '100%'
															}}
															inputStyles={{ width: '100%', marginBottom: '1rem' }}
															editable={true}
														/>
													</Grid>
												</Grid>
												<Grid container spacing={2} sx={{ marginTop: '1rem' }}>
													<Grid item xs={12}>
														<Typography variant="h6" fontWeight={600}>
															Video CTA (optional)
														</Typography>
														<Typography variant="body2" color="text.secondary">
															{video.ctaType === CTA_TYPES.VIDEO_LVL
																? 'Set both CTA fields to override the section CTA.'
																: 'The section CTA is selected. Video CTA fields are not used.'}
														</Typography>
														<Typography variant="body2" role="status" sx={{ marginTop: 1 }}>
															<span style={{ fontWeight: 600, color: 'red' }}>STATUS</span>:{' '}
															{getVideoCtaStatus(video)}
														</Typography>
													</Grid>
													<Grid item xs={12} md={4}>
														<DropDownSingle
															label="CTA Type"
															keyData="ctaType"
															list={[CTA_TYPES.VIDEO_LVL, CTA_TYPES.SECTION_LVL]}
															isObjectEditor={false}
															directValue={video.ctaType}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, { ctaType: value })
															}
															formControlStyles={{ width: '100%' }}
															labelStyles={{ width: '100%' }}
														/>
													</Grid>
													<Grid item xs={12} md={4}>
														<InputConfig
															isObjectEditor={false}
															keyData="videoLvlCtaDetails.ctaText"
															id={`video-cta-text-${config.id}-${videoIndex}`}
															label="CTA Text"
															placeholder="Read more"
															inputProps={{
																maxLength: TEXT_MAX_LENGTH,
																'aria-label': 'CTA Text'
															}}
															error={!!ctaErrors.ctaText}
															helperText={
																ctaErrors.ctaText ||
																`${(ctaDetails.ctaText || '').length}/${TEXT_MAX_LENGTH}`
															}
															labelStyles={{ width: '100%' }}
															directValue={ctaDetails.ctaText || ''}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, {
																	ctaType: video.ctaType || CTA_TYPES.VIDEO_LVL,
																	videoLvlCtaDetails: { ...ctaDetails, ctaText: value }
																})
															}
														/>
													</Grid>
													<Grid item xs={12} md={4}>
														<InputConfig
															isObjectEditor={false}
															keyData="videoLvlCtaDetails.ctaUrl"
															id={`video-cta-url-${config.id}-${videoIndex}`}
															label="CTA URL"
															inputType="url"
															placeholder="https://example.com/article"
															inputProps={{ 'aria-label': 'CTA URL' }}
															error={!!ctaErrors.ctaUrl}
															helperText={ctaErrors.ctaUrl || 'Use a full http:// or https:// URL.'}
															labelStyles={{ width: '100%' }}
															directValue={ctaDetails.ctaUrl || ''}
															updateDirectValue={(value) =>
																handleVideoFieldChange(videoIndex, {
																	ctaType: video.ctaType || CTA_TYPES.VIDEO_LVL,
																	videoLvlCtaDetails: { ...ctaDetails, ctaUrl: value }
																})
															}
														/>
													</Grid>
												</Grid>
											</Box>
										</Box>
									</React.Fragment>
								);
							})}
						<Grid item xs={6}>
							<AddCircleOutlineOutlinedIcon
								color="primary"
								fontSize="large"
								className={classes.icon}
								sx={{
									display: 'block',
									margin: '1rem auto 0 auto'
								}}
								onClick={() => handlePlaylistConfigAddVideo(config.id)}
							/>
						</Grid>
					</Box>
				</FormGroup>
			</Box>
		)
	);
};

export default SinglePageConfig;
